import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from 'react-dom';
import { useSearchParams, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { useSessionJwt } from '../hooks/useSessionJwt';
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTimeRange } from '../contexts/TimeRangeContext';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useApp } from '../contexts/AppContext';
import { useTraceList, normalizeV1Trace } from '../services/tracesApi';
import { classifierLabel, fetchClassifierDimensions, useClassifierTraceMatches } from '../services/classifiersApi';
import { fetchProblems } from '../services/problemRcaApi';
import { isInDemoMode } from '../services/demoDataService';
import ResizableTable from '../components/ResizableTable';
import { getTableRowStyle } from '../components/TablePrimitives';
import { layout, components, iconSize, iconShape, palette } from '../utils/designTokens';
import { formatRelativeTime, formatTokens, formatDuration, formatCost } from '../utils/formatters';
import { truncateText } from '../utils/traceIoParser';
import { FilterPills, ListPageLayout, PageHeader, ErrorState, Breadcrumb } from '../components';
import { enhanceTracesResponse } from '../services/demoDataService';
import { getSignalFilterDisplayLabel, getTraceSignalCounts, resolveSignalFilterKey, formatSubtypeLabel } from '../utils/signalUtils';
import { OUTCOME_VALUE_LABELS, OUTCOME_FILTER_HIDDEN_VALUES, getOutcomeLabel as getOutcomeCellLabel, getOutcomeColor as getOutcomeCellColor } from '../utils/outcomeUtils';
import { stripRemovedFilterParams } from '../utils/urlBuilder';

const ERROR_CLASSIFIER_KEY = 'error_detection';

// Mirrors OUTCOME_BUCKET_VALUES in Overview.js / _OUTCOME_TO_AGG_COL in
// workers/src/workflow/clickhouse_client.py — so a bucket drilldown click from
// the Top Business Tasks table (which sends a comma-joined value set) round-trips to
// the same friendly label ("Completed") the pill itself uses.
const OUTCOME_BUCKETS = [
  { label: 'Not Completed', values: ['failure'] },
  { label: 'Completed',     values: ['success', 'acknowledged', 'information'] },
  { label: 'Pending',   values: ['(none)', 'unknown'] },
];

const OUTCOME_FILTER_OPTIONS = Object.entries(OUTCOME_VALUE_LABELS)
  .filter(([value]) => !OUTCOME_FILTER_HIDDEN_VALUES.has(value))
  .map(([value, label]) => ({ value, label }))
  .sort((a, b) => a.label.localeCompare(b.label));

const getOutcomeFilterDisplayLabel = (value) => {
  if (!value) return '';
  const parts = value.split(',').map(v => v.trim()).filter(Boolean);
  if (parts.length === 0) return '';
  const bucketMatch = OUTCOME_BUCKETS.find(bucket => (
    bucket.values.length === parts.length && bucket.values.every(v => parts.includes(v))
  ));
  if (bucketMatch) return bucketMatch.label;
  return parts.map(v => OUTCOME_VALUE_LABELS[v] || v).join(' + ');
};

const shortProblemId = (value) => (value ? String(value).slice(0, 10) : 'unknown');

const getProblemOptionLabel = (problem) => (
  problem?.latest_rca?.title
  || problem?.latest_rca?.summary
  || problem?.primary_event_key
  || problem?.primary_subtype
  || `${classifierLabel(problem?.classifier_key)} problem ${shortProblemId(problem?.problem_id)}`
);

const getDimensionValue = (item) => (
  typeof item === 'string'
    ? item
    : String(item?.value || item?.primary_subtype || item?.primary_event_key || '')
);

const formatDimensionLabel = (value, classifierKey = null) => formatSubtypeLabel(value, classifierKey);

const dimensionOptions = (items) => (
  (Array.isArray(items) ? items : [])
    .map((item) => {
      const value = getDimensionValue(item);
      if (!value) return null;
      return { value, label: formatDimensionLabel(value), searchText: value };
    })
    .filter(Boolean)
    .sort((a, b) => a.label.localeCompare(b.label))
);

// ── Business task classification tooltip ──────────────────────────────────────

const WorkflowTooltip = ({ trace, anchorRef, visible }) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (visible && anchorRef.current) {
      const r = anchorRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: r.left });
    }
  }, [visible, anchorRef]);

  if (!visible) return null;

  const wf = trace.workflow || {};
  const fields = [
    { label: 'Business Task',   value: wf.workflow_name    || trace.workflow_name },
  ].filter(f => f.value);

  if (!fields.length) return null;

  return createPortal(
    <div style={{
      position: 'fixed',
      top: pos.top,
      left: pos.left,
      zIndex: 9999,
      background: 'var(--color-panel-tertiary)',
      border: '1px solid var(--color-border-subtle)',
      borderRadius: 8,
      padding: '10px 14px',
      minWidth: 220,
      maxWidth: 320,
      boxShadow: palette.quality.shadowCard,
      pointerEvents: 'none',
    }}>
      {fields.map(({ label, value }) => (
        <div key={label} style={{ display: 'flex', gap: 8, marginBottom: 4, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 10, color: 'var(--color-text-muted)', minWidth: 110, flexShrink: 0, paddingTop: 1 }}>
            {label}
          </span>
          <span style={{ fontSize: 11, color: 'var(--color-text-primary)', lineHeight: 1.4 }}>
            {value}
          </span>
        </div>
      ))}
    </div>,
    document.body
  );
};

// ── Business task cell with hover tooltip ────────────────────────────────────

const WorkflowCell = ({ trace }) => {
  const [hovered, setHovered] = useState(false);
  const ref = useRef(null);
  const workflowName = trace.workflow?.workflow_name || trace.workflow_name;

  return (
    <div className="flex flex-col" style={{ gap: 1 }}>
      <span
        ref={ref}
        className="text-sm truncate"
        style={{
          color: components.table.cell.colors.entity,
          cursor: workflowName ? 'default' : undefined,
        }}
        onMouseEnter={() => workflowName && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {workflowName || '—'}
      </span>
      <WorkflowTooltip trace={trace} anchorRef={ref} visible={hovered} />
    </div>
  );
};

const SIGNAL_PILL_VARIANTS = {
  danger: 'text-danger bg-danger/10',
  warning: 'text-warning bg-warning/10',
  info: 'text-accent-primary-base bg-accent-primary-base/10',
  neutral: 'text-text-muted bg-background-tertiary',
  warm: 'text-accent-warm-base bg-accent-warm-base/10',
};

const renderSignalLabel = ({ label, count }) => (count > 1 ? `${label} (${count})` : label);

const VirtualTraceRow = React.memo(({ data, index, style }) => {
  const { data: traces, columnWidths, onOpen: onSelect, selectedTraceId } = data;
  const trace = traces[index];
  const isSelected = trace.trace_id === selectedTraceId;
  const [isHovered, setIsHovered] = React.useState(false);

  // Parse input/output once for this row.
  // Read the server-computed column value directly (first_user_input / last_assistant_output);
  // no client-side re-extraction. Input/output arrive as a plain string (search) or {text} object.
  const inputText = React.useMemo(() => {
    const v = trace.input;
    return (typeof v === 'string' ? v : v?.text) ?? null;
  }, [trace.input]);
  const outputText = React.useMemo(() => {
    const v = trace.output;
    return (typeof v === 'string' ? v : v?.text) ?? null;
  }, [trace.output]);
  // Outcome is a property of a workflow. With no workflow (Workflow column shows
  // "—"), there's no outcome to be "pending" — mirror the "—" instead of
  // defaulting to "Pending", which reads as "still resolving". "Pending" is
  // reserved for traces that DO have a workflow with an unsealed outcome.
  const hasWorkflow = !!(trace.workflow?.workflow_name || trace.workflow_name);
  const rawOutcome = trace.workflow?.outcome || '';
  const outcomeLabel = hasWorkflow ? getOutcomeCellLabel(rawOutcome) : '—';
  const outcomeColor = hasWorkflow ? getOutcomeCellColor(rawOutcome) : palette.text.muted;
  const signalCounts = React.useMemo(() => getTraceSignalCounts(trace), [trace]);
  const visibleSignalCounts = React.useMemo(() => signalCounts.slice(0, 3), [signalCounts]);
  const hiddenSignalsCount = signalCounts.length - visibleSignalCounts.length;
  const signalTitle = React.useMemo(() => {
    if (signalCounts.length === 0) return undefined;
    return signalCounts.map(renderSignalLabel).join(', ');
  }, [signalCounts]);
  const relevanceScore = trace.relevance_score != null
    ? Math.round(trace.relevance_score * 100)
    : null;

  return (
    <div
      style={{
        ...style,
        ...getTableRowStyle(index, {
          isHovered,
          isSelected,
          interactive: true,
        }),
      }}
      className={`${components.table.row.container} ${components.table.row.border} cursor-pointer`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onSelect(trace)}
    >
      {/* Entity: Workflow */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.muted} truncate`}
        style={{ width: `${columnWidths.workflow}px`, minWidth: `${columnWidths.workflow}px`, flex: 'none', color: components.table.cell.colors.entity }}
      >
        <WorkflowCell trace={trace} />
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Time: Start Time */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.muted}`}
        style={{ width: `${columnWidths.startTime}px`, minWidth: `${columnWidths.startTime}px`, flex: 'none', color: components.table.cell.colors.time }}
      >
        {formatRelativeTime(trace.trace_start)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Input */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.body} truncate`}
        style={{ width: `${columnWidths.input}px`, minWidth: `${columnWidths.input}px`, flex: 'none', color: components.table.cell.colors.entity }}
        title={inputText || undefined}
      >
        {truncateText(inputText, 80)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Output */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.body} truncate`}
        style={{ width: `${columnWidths.output}px`, minWidth: `${columnWidths.output}px`, flex: 'none', color: components.table.cell.colors.entity }}
        title={outputText || undefined}
      >
        {truncateText(outputText, 80)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Outcome */}
      <div
        className={`${components.table.cell.padding} truncate`}
        style={{ width: `${columnWidths.outcome}px`, minWidth: `${columnWidths.outcome}px`, flex: 'none', color: outcomeColor }}
        title={outcomeLabel}
      >
        {outcomeLabel}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Signals */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.body} truncate`}
        style={{ width: `${columnWidths.quality}px`, minWidth: `${columnWidths.quality}px`, flex: 'none' }}
        title={signalTitle}
      >
        <div className="flex items-center gap-1.5 overflow-hidden whitespace-nowrap">
          {relevanceScore != null && (
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-medium border border-border-strong"
              style={{ color: palette.accents.primary.base, backgroundColor: `${palette.accents.primary.base}18` }}
              title={`Relevance: ${relevanceScore}%`}
            >
              {relevanceScore}% match
            </span>
          )}
          {signalCounts.length > 0 && visibleSignalCounts.map((signal, idx) => (
            <span
              key={`${signal.label}-${idx}`}
              className={`px-2 py-0.5 rounded-full text-[10px] font-medium border border-border-strong truncate ${SIGNAL_PILL_VARIANTS[signal.variant] || SIGNAL_PILL_VARIANTS.neutral}`}
            >
              {renderSignalLabel(signal)}
            </span>
          ))}
          {hiddenSignalsCount > 0 && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border border-border-strong ${SIGNAL_PILL_VARIANTS.neutral}`}>
              +{hiddenSignalsCount}
            </span>
          )}
          {relevanceScore == null && signalCounts.length === 0 && (
            <span className={`${components.table.cell.text.muted}`} style={{ color: components.table.cell.colors.metric }}>—</span>
          )}
        </div>
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Time: Latency */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.latency}px`, minWidth: `${columnWidths.latency}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {formatDuration(trace.duration_ms)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Tokens */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.tokens}px`, minWidth: `${columnWidths.tokens}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {formatTokens(trace.tokens || 0)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Cost */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.cost}px`, minWidth: `${columnWidths.cost}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {formatCost(trace.cost)}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* List: Models */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.body} truncate`}
        style={{ width: `${columnWidths.models}px`, minWidth: `${columnWidths.models}px`, flex: 'none', color: components.table.cell.colors.entity }}
        title={trace.models?.join(', ')}
      >
        {trace.models && trace.models.length > 0 ? trace.models.join(', ') : '—'}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* List: Agents */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.body} truncate`}
        style={{ width: `${columnWidths.agents}px`, minWidth: `${columnWidths.agents}px`, flex: 'none', color: components.table.cell.colors.entity }}
        title={trace.agents?.join(', ')}
      >
        {trace.agents && trace.agents.length > 0 ? trace.agents.join(', ') : '—'}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Span Count */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric}`}
        style={{ width: `${columnWidths.spans}px`, minWidth: `${columnWidths.spans}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {trace.span_count || '—'}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Metric: Tools */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.metric} flex items-center gap-1`}
        style={{ width: `${columnWidths.tools}px`, minWidth: `${columnWidths.tools}px`, flex: 'none', color: components.table.cell.colors.metric }}
      >
        {trace.tool_status ? (
          <>
            <span>{trace.tool_status.total_calls}</span>
            {trace.tool_status.issues > 0 && (
              <AlertTriangle size={14} className="text-warning" title={`${trace.tool_status.issues} issue${trace.tool_status.issues > 1 ? 's' : ''}`} />
            )}
          </>
        ) : (
          '—'
        )}
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Entity: Trace ID (first 8 chars) */}
      <div
        className={`flex items-center ${components.table.cell.gap} ${components.table.cell.padding}`}
        style={{ width: `${columnWidths.traceId}px`, minWidth: `${columnWidths.traceId}px`, flex: 'none' }}
      >
        <div className={`${iconSize.dot} ${iconShape.dot} flex-shrink-0 ${(trace.errors > 0 || trace.has_prompt_injection || trace.has_data_exfiltration || trace.tool_status?.issues > 0) ? 'bg-danger' : 'bg-accent-primary-base'}`}></div>
        <span className={`${components.table.cell.text.id} truncate`} style={{ color: components.table.cell.colors.entity }} title={trace.trace_id}>
          {trace.trace_id.substring(0, 8)}...
        </span>
      </div>
      <div style={{ width: '3px', flex: 'none' }} />
      {/* Entity: Session ID (first 8 chars) */}
      <div
        className={`${components.table.cell.padding} ${components.table.cell.text.muted} truncate`}
        style={{ width: `${columnWidths.sessionId}px`, minWidth: `${columnWidths.sessionId}px`, flex: 'none', color: components.table.cell.colors.entity }}
        title={trace.session_id || undefined}
      >
        {trace.session_id ? `${trace.session_id.substring(0, 8)}...` : '—'}
      </div>
    </div>
  );
});

export default function Traces({ searchResults = null, filterValues = {}, onAddFilter, onRemoveFilter, onEntitiesUpdate }) {
  const { selectedAppId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { displayLabel, selectedRange } = useTimeRange();
  const { selectedEnvironment, environments } = useEnvironment();
  const { selectedAppName } = useApp();
  const { session } = useStytchMemberSession();
  const { sessionId, getJwt } = useSessionJwt();
  const selectedTraceId = searchParams.get('selectedTraceId');
  const navigate = useNavigate();

  // Standalone mode when no parent filter handlers are provided (direct route navigation)
  const isStandalone = !onAddFilter;

  useEffect(() => {
    const strippedParams = stripRemovedFilterParams(searchParams);
    if (strippedParams) {
      setSearchParams(strippedParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Destructure filter values from props (when embedded in TelemetryTab)
  // or fall back to reading from URL (when used standalone)
  const {
    sessionIdFilter: _sessionId = '',
    userFilter: _user = '',
    agentFilter: _agent = '',
    toolFilter: _tool = '',
    modelFilter: _model = '',
    workflowFilter: _workflow = '',
    outcomeFilter: _outcome = '',
    signalGroupFilter: propSignalGroupFilter = '',
    signalFilter: propSignalFilter = '',
  } = filterValues;

  // In standalone mode, read all entity filters from URL params too
  const sessionIdFilter = _sessionId || (isStandalone ? searchParams.get('sessionId') || '' : '');
  const userFilter      = _user      || (isStandalone ? searchParams.get('user')      || '' : '');
  const agentFilter = _agent || (isStandalone ? searchParams.get('agent') || '' : '');
  const toolFilter = _tool || (isStandalone ? searchParams.get('tool') || '' : '');
  const modelFilter = _model || (isStandalone ? searchParams.get('model') || '' : '');
  const workflowFilter = _workflow || (isStandalone ? searchParams.get('workflow') || '' : '');
  const outcomeFilter = _outcome || (isStandalone ? searchParams.get('outcome') || '' : '');

  // Standalone mode: read signal filters and drilldown params from URL
  const signalGroupFilter = propSignalGroupFilter || searchParams.get('signal_group') || searchParams.get('signalGroup') || '';
  const signalFilter = propSignalFilter || searchParams.get('signal') || '';
  const subtypeFilter = isStandalone ? searchParams.get('subtype') || searchParams.get('primary_subtype') || '' : '';
  const eventKeyFilter = isStandalone ? searchParams.get('event_key') || searchParams.get('primary_event_key') || '' : '';
  const urlClassifierFilter = isStandalone ? searchParams.get('classifier') || '' : '';
  // A subtype/event_key drilldown targets a specific classifier. Derive it from the
  // `signal` param (which carries the classifier key, e.g. loop_detection) so the
  // /classifiers/{key}/traces query hits the right classifier. No fallback: every
  // drilldown emits `signal`, so if it is missing that is a bug to surface — do not
  // silently substitute a classifier (the old code hardcoded error_detection, which
  // returned empty for every non-error signal since their detections live elsewhere).
  const classifierFilter = urlClassifierFilter
    || (isStandalone && (subtypeFilter || eventKeyFilter)
        ? (resolveSignalFilterKey(signalFilter) || signalFilter)
        : '');
  const classifierStatusFilter = isStandalone ? searchParams.get('classifier_status') || 'detected' : 'detected';
  const problemIdFilter = isStandalone ? searchParams.get('problem_id') || '' : '';
  // workflow_id is an id-precise companion to workflowFilter (name) — set by
  // drilldowns that already know the exact workflow (e.g. Overview Problems table),
  // used to hit the indexed workflow_id column on trace_classifier_results_v2
  // instead of a full scan on workflow_name.
  const workflowIdFilter = isStandalone ? searchParams.get('workflow_id') || '' : '';

  // Read drilldown parameters from URL (start_time, end_time, min_latency, max_latency, sort)
  const startTimeFilter = searchParams.get('start_time');
  const endTimeFilter = searchParams.get('end_time');
  const minLatencyFilter = searchParams.get('min_latency');
  const maxLatencyFilter = searchParams.get('max_latency');
  const sortFilter = searchParams.get('sort');


  const tenantId = session?.organization_id ?? null;
  const isDemo = isInDemoMode(tenantId, selectedEnvironment, selectedAppId);

  // Server-side filter object passed to the hook.
  // Signal/sort are excluded here (handled separately) so active filter pills
  // don't silently do nothing.
  const traceFilters = useMemo(() => {
    const base = {
      sessionId:       sessionIdFilter,
      userId:          userFilter,
      agent:           agentFilter,
      tool:            toolFilter,
      model:           modelFilter,
      workflow:        workflowFilter,
      // Disambiguates duplicate canonical_name values across distinct workflows —
      // preferred over the name server-side whenever both are present.
      workflowId:      workflowIdFilter,
      outcome:         outcomeFilter,
      problemId:       problemIdFilter,
      startTime:       startTimeFilter,
      endTime:         endTimeFilter,
      minLatency:      minLatencyFilter,
      maxLatency:      maxLatencyFilter,
    };
    return base;
  }, [sessionIdFilter, userFilter, agentFilter, toolFilter, modelFilter, workflowFilter,
      workflowIdFilter, outcomeFilter,
      problemIdFilter, signalGroupFilter, signalFilter, startTimeFilter, endTimeFilter,
      minLatencyFilter, maxLatencyFilter, sortFilter]);

  const {
    traces: apiTraces, loading, error, hasMore, page,
    loadNextPage, loadPrevPage, entities, info,
  } = useTraceList(selectedAppId, traceFilters, { enabled: !isDemo && !classifierFilter });

  const {
    traces: classifierTraces,
    loading: classifierLoading,
    error: classifierError,
  } = useClassifierTraceMatches(selectedAppId, classifierFilter, {
    enabled: !isDemo && Boolean(classifierFilter),
    status: classifierStatusFilter,
    subtype: subtypeFilter,
    eventKey: eventKeyFilter,
    workflow: workflowFilter,
    workflowId: workflowIdFilter,
    problemId: problemIdFilter,
  });

  // Demo path: use enhanced mock data normalized to canonical shape.
  const tracesData = useMemo(() => {
    if (isDemo) {
      const demoRaw = enhanceTracesResponse(null, tenantId, selectedEnvironment, selectedAppId, problemIdFilter);
      return (demoRaw?.traces || []).map(normalizeV1Trace);
    }
    if (classifierFilter) {
      return classifierTraces;
    }
    return apiTraces;
  }, [isDemo, tenantId, selectedEnvironment, selectedAppId, problemIdFilter, classifierFilter, classifierTraces, apiTraces]);

  const allAgents    = entities.agents;
  const allTools     = entities.tools;
  const allModels    = entities.models;
  const allWorkflows = entities.workflows;

  const [problems, setProblems] = useState([]);
  const [errorDimensions, setErrorDimensions] = useState({ subtypes: [], event_keys: [], events_by_subtype: {} });

  useEffect(() => {
    const jwt = getJwt();
    if (!isStandalone || isDemo || !jwt || !selectedAppId || !selectedEnvironment) {
      setProblems([]);
      return undefined;
    }

    let ignore = false;
    fetchProblems(selectedAppId, selectedEnvironment, selectedRange, jwt, {
      limit: 100,
    })
      .then((response) => {
        if (!ignore) setProblems(response.problems || []);
      })
      .catch((err) => {
        if (!ignore) setProblems([]);
      });

    return () => { ignore = true; };
  }, [isStandalone, isDemo, selectedAppId, selectedEnvironment, selectedRange, sessionId, getJwt]);

  useEffect(() => {
    const jwt = getJwt();
    if (!isStandalone || isDemo || !jwt || !selectedAppId || !selectedEnvironment) {
      setErrorDimensions({ subtypes: [], event_keys: [], events_by_subtype: {} });
      return undefined;
    }

    const abortController = new AbortController();
    fetchClassifierDimensions(selectedAppId, ERROR_CLASSIFIER_KEY, jwt, {
      signal: abortController.signal,
      env: selectedEnvironment,
      range: selectedRange,
    })
      .then((data) => {
        setErrorDimensions({
          subtypes: Array.isArray(data?.subtypes) ? data.subtypes : [],
          event_keys: Array.isArray(data?.event_keys) ? data.event_keys : [],
          events_by_subtype: data?.events_by_subtype || {},
        });
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') setErrorDimensions({ subtypes: [], event_keys: [], events_by_subtype: {} });
      });

    return () => abortController.abort();
  }, [isStandalone, isDemo, selectedAppId, selectedEnvironment, selectedRange, sessionId, getJwt]);

  // Extract unique session IDs for autocomplete
  const sessionOptions = useMemo(() => {
    if (!tracesData) return [];
    const sessions = tracesData
      .map(trace => trace.session_id)
      .filter(Boolean) // Remove null/undefined values
      .filter(sessionId => sessionId.length > 0); // Remove empty strings

    return [...new Set(sessions)]; // Remove duplicates
  }, [tracesData]);

  // Use all available agents for autocomplete suggestions
  const agentOptions = useMemo(() => {
    return allAgents.sort();
  }, [allAgents]);

  // Extract unique tools for autocomplete
  const toolOptions = useMemo(() => {
    return allTools.sort();
  }, [allTools]);

  // Extract unique models for autocomplete
  const modelOptions = useMemo(() => {
    return allModels.sort();
  }, [allModels]);

  // Extract unique workflows for autocomplete
  const workflowOptions = useMemo(() => {
    return allWorkflows.sort();
  }, [allWorkflows]);

  // Signal (classifier) filter options for the "Add Filter" dropdown — {value: classifier key, label}
  // pairs so onAddFilter('classifier', key) receives the raw key the backend expects, not the
  // display label. Derived from classifiers detected on the currently-loaded traces page, same as
  // sessionOptions above — there's no lifetime/full-range classifier list endpoint wired here yet.
  const classifierFilterOptions = useMemo(() => {
    if (!Array.isArray(tracesData)) return [];

    const keys = new Set();
    const addKey = (value) => {
      const resolved = resolveSignalFilterKey(value);
      if (!resolved) return;
      keys.add(resolved);
    };

    tracesData.forEach((trace) => {
      (Array.isArray(trace?.detections) ? trace.detections : []).forEach((detection) => {
        addKey(detection?.classifier_key || detection?.metric || detection?.name);
      });

      (Array.isArray(trace?.detected_classifiers) ? trace.detected_classifiers : []).forEach((classifier) => {
        if (typeof classifier === 'string') {
          addKey(classifier);
          return;
        }
        addKey(classifier?.classifier_key || classifier?.metric || classifier?.name);
      });

      (Array.isArray(trace?.problem_types) ? trace.problem_types : []).forEach(addKey);
      (Array.isArray(trace?.signals) ? trace.signals : []).forEach(addKey);
      (Array.isArray(trace?.signal) ? trace.signal : []).forEach(addKey);
    });

    return [...keys]
      .map(key => ({ value: key, label: getSignalFilterDisplayLabel(key) || classifierLabel(key) }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tracesData]);

  const problemOptions = useMemo(() => (
    (Array.isArray(problems) ? problems : [])
      .map((problem) => {
        const value = problem?.problem_id || '';
        if (!value) return null;
        const label = getProblemOptionLabel(problem);
        return {
          value,
          label,
          searchText: [
            label,
            value,
            classifierLabel(problem?.classifier_key),
            problem?.latest_rca?.summary,
            problem?.latest_rca?.title,
            problem?.primary_subtype,
            problem?.primary_event_key,
          ].filter(Boolean).join(' '),
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label))
  ), [problems]);

  const problemLabelById = useMemo(() => {
    const labels = new Map();
    problemOptions.forEach((option) => labels.set(option.value, option.label));
    return labels;
  }, [problemOptions]);

  const errorSubtypeOptions = useMemo(
    () => dimensionOptions(errorDimensions.subtypes),
    [errorDimensions.subtypes],
  );

  const errorEventKeyOptions = useMemo(() => {
    const scopedEvents = subtypeFilter
      ? errorDimensions.events_by_subtype?.[subtypeFilter]
      : null;
    return dimensionOptions(scopedEvents || errorDimensions.event_keys);
  }, [errorDimensions.event_keys, errorDimensions.events_by_subtype, subtypeFilter]);

  // Keep a canonical URL key for signal group filtering while supporting legacy links.
  useEffect(() => {
    const legacySignalGroup = searchParams.get('signalGroup');
    if (!legacySignalGroup) return;

    const normalizedParams = new URLSearchParams(searchParams);
    if (!normalizedParams.get('signal_group')) {
      normalizedParams.set('signal_group', legacySignalGroup);
    }
    normalizedParams.delete('signalGroup');
    setSearchParams(normalizedParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const legacySubtype = searchParams.get('primary_subtype');
    const legacyEventKey = searchParams.get('primary_event_key');
    if (!legacySubtype && !legacyEventKey) return;

    const normalizedParams = new URLSearchParams(searchParams);
    if (legacySubtype && !normalizedParams.get('subtype')) {
      normalizedParams.set('subtype', legacySubtype);
    }
    if (legacyEventKey && !normalizedParams.get('event_key')) {
      normalizedParams.set('event_key', legacyEventKey);
    }
    normalizedParams.delete('primary_subtype');
    normalizedParams.delete('primary_event_key');
    setSearchParams(normalizedParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Apply filters client-side.
  // In search mode: entity filters apply to the already-retrieved semantic results.
  // In normal mode: KPI filters apply after the server handles entity filtering.
  const filteredTraces = useMemo(() => {
    if (searchResults !== null) {
      let results = searchResults;
      if (sessionIdFilter) results = results.filter(t => t.session_id === sessionIdFilter);
      if (userFilter)      results = results.filter(t => t.user_id === userFilter);
      if (agentFilter)     results = results.filter(t => Array.isArray(t.agents) && t.agents.includes(agentFilter));
      if (toolFilter)      results = results.filter(t => Array.isArray(t.tools)  && t.tools.includes(toolFilter));
      if (modelFilter)     results = results.filter(t => Array.isArray(t.models) && t.models.includes(modelFilter));
      return results;
    }

    if (!tracesData) return null;
    let traces = tracesData;


    return traces;
  }, [searchResults, tracesData, sessionIdFilter, userFilter, agentFilter, toolFilter, modelFilter, outcomeFilter]);

  const handleTraceSelect = useCallback((trace) => {
    if (!trace) throw new Error('handleTraceSelect requires trace');

    // Build referrer URL with current filters AND selected trace ID
    const referrerParams = new URLSearchParams(location.search);
    referrerParams.set('selectedTraceId', trace.trace_id);
    const referrerUrl = `${location.pathname}?${referrerParams.toString()}`;
    const referrerBreadcrumbItems = Array.isArray(location.state?.referrerBreadcrumbItems)
      ? [
          ...location.state.referrerBreadcrumbItems.filter(item => item?.label),
          { label: 'Traces', to: referrerUrl },
        ]
      : null;

    // Build trace detail URL with consistent param order (env, range)
    const traceParams = new URLSearchParams();
    if (selectedEnvironment) traceParams.set('env', selectedEnvironment);
    if (selectedRange) traceParams.set('range', selectedRange);

    navigate(`/app/${selectedAppId}/traces/${encodeURIComponent(trace.trace_id)}?${traceParams.toString()}`, {
      state: {
        trace,
        referrerUrl,
        ...(location.state?.referrerLabel ? { referrerLabel: location.state.referrerLabel } : {}),
        ...(referrerBreadcrumbItems ? { referrerBreadcrumbItems } : {}),
      }
    });
  }, [navigate, location, selectedAppId, selectedEnvironment, selectedRange]);

  const handleNextPage = loadNextPage;
  const handlePrevPage = loadPrevPage;
  const effectiveLoading = classifierFilter ? classifierLoading : loading;
  const effectiveError = classifierFilter ? classifierError : error;
  const effectiveHasMore = classifierFilter ? false : hasMore;
  const effectivePage = classifierFilter ? 1 : page;
  const effectiveNextPage = classifierFilter ? () => {} : handleNextPage;
  const effectivePrevPage = classifierFilter ? () => {} : handlePrevPage;

  // Get referrer URL from location state (only exists when navigated from another page)
  const referrerUrl = location.state?.referrerUrl;

  // Resolve app name (for breadcrumb display)
  const appName = useMemo(() => {
    // Search in environments cache (always up-to-date with selectedAppId)
    if (selectedAppId && environments.length) {
      for (const env of environments) {
        const app = env.apps?.find(a => a.id === selectedAppId);
        if (app) return app.name;
      }
    }

    // Fallback: Try AppContext
    if (selectedAppName) return selectedAppName;

    // Last resort: use ID
    return selectedAppId;
  }, [selectedAppId, selectedAppName, environments]);

  // Breadcrumb items (show when navigated from Sessions or Business Tasks)
  const breadcrumbItems = useMemo(() => {
    // Preserve env and range params in breadcrumb links
    const params = new URLSearchParams();
    if (selectedEnvironment) params.set('env', selectedEnvironment);
    if (selectedRange) params.set('range', selectedRange);
    const queryString = params.toString() ? `?${params.toString()}` : '';
    const overviewParams = new URLSearchParams(params);
    overviewParams.set('tab', 'overview');
    const overviewQueryString = `?${overviewParams.toString()}`;
    const rcaParams = new URLSearchParams(params);
    rcaParams.set('tab', 'rca');
    const selectedRcaId = searchParams.get('rca_id') || '';
    if (selectedRcaId) rcaParams.set('rca_id', selectedRcaId);
    const rcaQueryString = `?${rcaParams.toString()}`;

    // Check if user came from Cost, Performance, or Journeys
    const fromPage = searchParams.get('from');
    const fromJourney = location.state?.fromJourney;
    const journeyReferrerUrl = location.state?.referrerUrl;
    const referrerBreadcrumbItems = Array.isArray(location.state?.referrerBreadcrumbItems)
      ? location.state.referrerBreadcrumbItems.filter(item => item?.label)
      : [];
    const selectedNode = location.state?.selectedNode;
    const selectedNodeType = location.state?.selectedNodeType;

    // From Sankey node/link drilldown. Crumb reads "Agents" — the top-level tab
    // it links back to (referrerUrl is the Sankey at tab=agents&subtab=journeys).
    if (fromJourney && journeyReferrerUrl) {
      const breadcrumb = [
        { label: appName, to: `/app/${selectedAppId}${queryString}` },
        { label: 'Agents', to: journeyReferrerUrl },
      ];

      // Add node name if available
      if (selectedNode) {
        breadcrumb.push({ label: `${selectedNodeType}: ${selectedNode}` });
      } else {
        breadcrumb.push({ label: 'Traces' });
      }

      return breadcrumb;
    }

    // From Cost tab drill-down (from=cost in URL)
    if (fromPage === 'cost') {
      const costParams = new URLSearchParams(params);
      costParams.set('tab', 'cost');
      const costQueryString = `?${costParams.toString()}`;
      return [
        { label: appName, to: `/app/${selectedAppId}${queryString}` },
        { label: 'Cost', to: `/app/${selectedAppId}${costQueryString}` },
        { label: 'Traces' },
      ];
    }

    // From Performance page drill-down (from=performance in URL)
    if (fromPage === 'performance') {
      const performanceParams = new URLSearchParams(params);
      performanceParams.set('tab', 'performance');
      const performanceQueryString = `?${performanceParams.toString()}`;
      return [
        { label: appName, to: `/app/${selectedAppId}${queryString}` },
        { label: 'Performance', to: `/app/${selectedAppId}${performanceQueryString}` },
        { label: 'Traces' },
      ];
    }

    // From a Tools/Models sub-tab row drill-down (from=journeys-<subtab>).
    // Back-crumb reads "Agents" — the top-level tab it links to (subtab=tools|models),
    // not the Journeys/Sankey sub-tab.
    if (fromPage && fromPage.startsWith('journeys-')) {
      const sub = fromPage.slice('journeys-'.length); // 'tools' | 'models'
      const agentsParams = new URLSearchParams(params);
      agentsParams.set('tab', 'agents');
      if (sub) agentsParams.set('subtab', sub);
      const agentsQueryString = `?${agentsParams.toString()}`;
      return [
        { label: appName, to: `/app/${selectedAppId}${queryString}` },
        { label: 'Agents', to: `/app/${selectedAppId}${agentsQueryString}` },
        { label: 'Traces' },
      ];
    }

    // From Problems drill-downs. Primary value is 'problems'; any legacy
    // 'overview-*' alias (signals/workflows/topics/intents/…) still resolves here
    // so older bookmarked/shared links keep the correct breadcrumb.
    if (fromPage === 'problems' || (fromPage && fromPage.startsWith('overview-'))) {
      return [
        { label: appName, to: `/app/${selectedAppId}${queryString}` },
        { label: 'Problems', to: `/app/${selectedAppId}${overviewQueryString}` },
        { label: 'Traces' },
      ];
    }

    if (fromPage === 'rca') {
      return [
        { label: appName, to: `/app/${selectedAppId}${queryString}` },
        { label: 'RCA', to: `/app/${selectedAppId}${rcaQueryString}` },
        { label: 'Traces' },
      ];
    }

    if (fromPage === 'signal-investigation' && referrerBreadcrumbItems.length > 0) {
      return [
        ...referrerBreadcrumbItems,
        { label: 'Traces' },
      ];
    }

    // Session filter from navigation state (from URL: /app/{appId}/traces?sessionId=xxx)
    if (sessionIdFilter) {
      return [
        { label: appName, to: `/app/${selectedAppId}${queryString}` },
        { label: 'Sessions', to: referrerUrl || `/app/${selectedAppId}/sessions${queryString}` },
        { label: 'Traces' },
      ];
    }
    // Workflow filter (legacy from workflows page)
    if (workflowFilter && referrerUrl) {
      return [
        { label: '← Business Tasks', to: referrerUrl },
        { label: workflowFilter },
      ];
    }
    // App-scoped traces (from URL: /app/{appId}/traces) - Show breadcrumb for consistency
    if (selectedAppId && !sessionIdFilter && !workflowFilter) {
      return [
        { label: appName, to: `/app/${selectedAppId}${queryString}` },
        { label: 'Traces' },
      ];
    }
    return null;
  }, [selectedAppId, appName, sessionIdFilter, workflowFilter, referrerUrl, selectedEnvironment, selectedRange, searchParams, location]);

  // Memoize itemData for virtualization to prevent unnecessary re-renders
  const itemData = useMemo(
    () => ({ traces: filteredTraces, onSelect: handleTraceSelect, selectedTraceId }),
    [filteredTraces, handleTraceSelect, selectedTraceId]
  );

  // Bubble entities + signals up to TelemetryTab whenever the trace list refreshes.
  useEffect(() => {
    if (!onEntitiesUpdate || !tracesData) return;
    const signalKeys = new Set();
    tracesData.forEach(trace => {
      (trace?.signals    || []).forEach(s => { const k = resolveSignalFilterKey(s);                                     if (k) signalKeys.add(k); });
      (trace?.detections || []).forEach(d => { const k = resolveSignalFilterKey(d?.classifier_key || d?.name); if (k) signalKeys.add(k); });
    });
    const freshSignals = [...signalKeys].map(k => getSignalFilterDisplayLabel(k)).filter(Boolean).sort((a, b) => a.localeCompare(b));
    onEntitiesUpdate({
      agents:    entities.agents,
      tools:     entities.tools,
      models:    entities.models,
      workflows: entities.workflows,
      signals:   freshSignals,
    });
  }, [tracesData, entities, onEntitiesUpdate]);

  // Format quality bucket label (cumulative)
  const formatQualityBucketLabel = (bucket) => {
    const labels = {
      'p20': 'Quality: ≤P20',
      'p40': 'Quality: ≤P40',
      'p60': 'Quality: ≤P60',
      'p80': 'Quality: ≤P80'
    };
    return labels[bucket] || bucket;
  };

  // Internal filter handlers for standalone mode (direct route navigation)
  const _internalAddFilter = useCallback((filterKey, value) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set(filterKey, String(value));
    if (filterKey === 'subtype' || filterKey === 'event_key') {
      newParams.set('classifier', ERROR_CLASSIFIER_KEY);
      newParams.set('classifier_status', 'detected');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const _internalRemoveFilter = useCallback((filterKey) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(filterKey);
    if (filterKey === 'workflow') {
      // workflow_id is a companion param (preferred over the name in
      // useClassifierTraceMatches) — must be cleared together or the
      // workflow scoping silently survives removal of the "Business Task" chip.
      newParams.delete('workflow_id');
    }
    if (filterKey === 'classifier') {
      newParams.delete('subtype');
      newParams.delete('event_key');
      newParams.delete('primary_subtype');
      newParams.delete('primary_event_key');
      newParams.delete('classifier_status');
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleAddFilter = onAddFilter || _internalAddFilter;
  const handleRemoveFilter = onRemoveFilter || _internalRemoveFilter;

  // Filter management functions
  const getActiveFilters = () => {
    const filters = [];
    // All filters are now managed through useFilterManager with URL sync
    if (sessionIdFilter) filters.push({ key: 'sessionId', label: 'Session ID', value: sessionIdFilter });
    if (agentFilter) filters.push({ key: 'agent', label: 'Agent', value: agentFilter });
    if (toolFilter) filters.push({ key: 'tool', label: 'Tool', value: toolFilter });
    if (modelFilter) filters.push({ key: 'model', label: 'Model', value: modelFilter });
    if (workflowFilter) filters.push({ key: 'workflow', label: 'Business Task', value: workflowFilter });
    if (outcomeFilter) {
      filters.push({
        key: 'outcome',
        label: 'Outcome',
        value: outcomeFilter,
        displayValue: getOutcomeFilterDisplayLabel(outcomeFilter),
      });
    }
    if (signalGroupFilter) {
      filters.push({ key: 'signal_group', label: 'Signal Group', value: signalGroupFilter });
    }
    if (signalFilter) filters.push({ key: 'signal', label: 'Signal', value: getSignalFilterDisplayLabel(signalFilter) || signalFilter });
    // classifierFilter is derived from signalFilter for signal drilldowns, so it usually
    // resolves to the same key — only show a separate Signal pill when it differs (e.g. an
    // explicit ?classifier= with no signal), otherwise it duplicates the pill above.
    if (classifierFilter && resolveSignalFilterKey(classifierFilter) !== resolveSignalFilterKey(signalFilter)) {
      filters.push({
        key: 'classifier',
        label: 'Signal Type',
        value: getSignalFilterDisplayLabel(classifierFilter) || classifierLabel(classifierFilter),
      });
    }
    if (subtypeFilter) filters.push({ key: 'subtype', label: 'Error Signal', value: subtypeFilter, displayValue: formatDimensionLabel(subtypeFilter, classifierFilter) });
    if (eventKeyFilter) filters.push({ key: 'event_key', label: 'Error Event', value: eventKeyFilter, displayValue: formatDimensionLabel(eventKeyFilter, classifierFilter) });
    if (problemIdFilter) {
      filters.push({
        key: 'problem_id',
        label: 'Problem',
        value: problemIdFilter,
        displayValue: problemLabelById.get(problemIdFilter) || shortProblemId(problemIdFilter),
      });
    }
    return filters;
  };

  const getAvailableFilters = () => {
    const available = [];
    if (!sessionIdFilter)       available.push({ key: 'sessionId',        label: 'Session ID',   options: sessionOptions });
    if (!agentFilter)           available.push({ key: 'agent',            label: 'Agent',        options: agentOptions });
    if (!toolFilter)            available.push({ key: 'tool',             label: 'Tool',         options: toolOptions });
    if (!modelFilter)           available.push({ key: 'model',            label: 'Model',        options: modelOptions });
    if (!workflowFilter)        available.push({ key: 'workflow',         label: 'Business Task', options: workflowOptions });
    if (!outcomeFilter)         available.push({ key: 'outcome',          label: 'Outcome',       options: OUTCOME_FILTER_OPTIONS });
    if (!subtypeFilter)         available.push({ key: 'subtype',          label: 'Error Signal', options: errorSubtypeOptions.length > 0 ? errorSubtypeOptions : undefined });
    if (!eventKeyFilter)        available.push({ key: 'event_key',        label: 'Error Event',   options: errorEventKeyOptions.length > 0 ? errorEventKeyOptions : undefined });
    if (!problemIdFilter && problemOptions.length > 0) {
      available.push({ key: 'problem_id', label: 'Problem', options: problemOptions });
    }
    if (!classifierFilter && classifierFilterOptions.length > 0) {
      available.push({ key: 'classifier', label: 'Signal', options: classifierFilterOptions });
    }
    return available;
  };

  if (effectiveError) {
    return <ErrorState title="Error Loading Traces" message={effectiveError} />;
  }

  const filtersContent = (
    <FilterPills
      activeFilters={getActiveFilters()}
      availableFilters={getAvailableFilters()}
      onAddFilter={handleAddFilter}
      onRemoveFilter={handleRemoveFilter}
    />
  );

  const tableContent = (
    <ResizableTable
      columns={[
        { key: 'workflow', label: 'Business Task', defaultWidthPercent: 4, minWidth: 110 },
        { key: 'startTime', label: 'Start Time', defaultWidthPercent: 5, minWidth: 110 },
        { key: 'input', label: 'Input', defaultWidthPercent: 20, minWidth: 280 },
        { key: 'output', label: 'Output', defaultWidthPercent: 18, minWidth: 280 },
        { key: 'outcome', label: 'Outcome', defaultWidthPercent: 5, minWidth: 90 },
        { key: 'quality', label: 'Signals', defaultWidthPercent: 11, minWidth: 200 },
        { key: 'latency', label: 'Latency (s)', defaultWidthPercent: 5, minWidth: 100 },
        { key: 'tokens', label: 'Tokens', defaultWidthPercent: 5, minWidth: 100 },
        { key: 'cost', label: 'Cost ($)', defaultWidthPercent: 4, minWidth: 90 },
        { key: 'models', label: 'Models', defaultWidthPercent: 10, minWidth: 160 },
        { key: 'agents', label: 'Agents', defaultWidthPercent: 10, minWidth: 180 },
        { key: 'spans', label: 'Spans', defaultWidthPercent: 3, minWidth: 70 },
        { key: 'tools', label: 'Tools', defaultWidthPercent: 4, minWidth: 80 },
        { key: 'traceId', label: 'Trace ID', defaultWidthPercent: 7, minWidth: 120 },
        { key: 'sessionId', label: 'Session ID', defaultWidthPercent: 7, minWidth: 120 },
      ]}
      data={filteredTraces}
      renderRow={VirtualTraceRow}
      onOpen={handleTraceSelect}
      selectedTraceId={selectedTraceId}
      storageKey="lumoz-traces-table-widths"
      getRowHeight={() => 44}
      containerHeight={filterValues && Object.keys(filterValues).length > 0 ? 'calc(100vh - 300px)' : 'calc(100vh - 410px)'}
    />
  );

  // Embedded in TelemetryTab — pure display table with stats
  if (filterValues && Object.keys(filterValues).length > 0) {
    return (
      <div className="flex flex-col h-full gap-3">
        {!effectiveLoading && searchResults !== null && filteredTraces !== null && (
          <div className="flex-shrink-0 flex items-center gap-2">
            <span style={{ fontSize: 12, color: palette.text.muted }}>
              {filteredTraces.length} result{filteredTraces.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        <div className="flex-1 min-h-0">
          {effectiveLoading || filteredTraces === null ? (
            <div className={components.table.empty}>Loading traces...</div>
          ) : filteredTraces.length === 0 ? (
            <div className={components.table.empty}>
              {searchResults !== null ? 'No traces found for search results' : 'No traces found'}
            </div>
          ) : (
            tableContent
          )}
        </div>
        {/* Slim footer: page window + chevrons, right-aligned. classifierFilter is
            always empty in embedded mode (requires isStandalone), so no guard needed.
            Totals live in the Explore delta line, so the window is enough here; the
            "of N" only appears on the last page, where the count is exact. */}
        {!effectiveLoading && searchResults === null && filteredTraces !== null && filteredTraces.length > 0 && (() => {
          const start = (effectivePage - 1) * 50 + 1;
          const end = (effectivePage - 1) * 50 + filteredTraces.length;
          return (
            <div className="flex-shrink-0 flex items-center justify-end gap-2 px-2" style={{ height: 36 }}>
              <span style={{ fontSize: 12, color: palette.text.muted }}>
                {start}–{end}{!effectiveHasMore ? ` of ${end}` : ''} traces
              </span>
              <button
                onClick={effectivePrevPage}
                disabled={effectivePage === 1 || effectiveLoading}
                aria-label="Previous page"
                className="p-1 rounded text-text-supporting hover:bg-background-tertiary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={effectiveNextPage}
                disabled={!effectiveHasMore || effectiveLoading}
                aria-label="Next page"
                className="p-1 rounded text-text-supporting hover:bg-background-tertiary disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          );
        })()}
      </div>
    );
  }

  return (
    <ListPageLayout
      breadcrumb={breadcrumbItems && <Breadcrumb items={breadcrumbItems} />}
      header={
        <PageHeader
          title="Traces"
          timeLabel={displayLabel}
        >
          {(sessionIdFilter || agentFilter || toolFilter || modelFilter || workflowFilter || outcomeFilter || signalGroupFilter || signalFilter || classifierFilter) && (
            <div className={`${components.pageHeader.titleContainer} ml-3`}>
              {sessionIdFilter && <span className={components.pageHeader.contextMetadata}>Session ID: {sessionIdFilter}</span>}
              {agentFilter && <span className={components.pageHeader.contextMetadata}>Agent: {agentFilter}</span>}
              {toolFilter && <span className={components.pageHeader.contextMetadata}>Tool: {toolFilter}</span>}
              {modelFilter && <span className={components.pageHeader.contextMetadata}>Model: {modelFilter}</span>}
              {workflowFilter && <span className={components.pageHeader.contextMetadata}>Business Task: {workflowFilter}</span>}
              {outcomeFilter && <span className={components.pageHeader.contextMetadata}>Outcome: {getOutcomeFilterDisplayLabel(outcomeFilter)}</span>}
              {signalGroupFilter && <span className={components.pageHeader.contextMetadata}>Signal Group: {signalGroupFilter}</span>}
              {signalFilter && <span className={components.pageHeader.contextMetadata}>Signal: {getSignalFilterDisplayLabel(signalFilter) || signalFilter}</span>}
              {classifierFilter && <span className={components.pageHeader.contextMetadata}>Signal: {getSignalFilterDisplayLabel(classifierFilter) || classifierLabel(classifierFilter)}</span>}
            </div>
          )}
        </PageHeader>
      }
      filters={filtersContent}
      loading={effectiveLoading}
      error={effectiveError}
      info={info}
      data={filteredTraces}
      pagination={{
        page: effectivePage,
        limit: 50,
        hasMore: effectiveHasMore,
        onNext: effectiveNextPage,
        onPrev: effectivePrevPage,
        loading: effectiveLoading
      }}
    >
      {tableContent}
    </ListPageLayout>
  );
}
